import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as con from "../../GlobalConstants";
import { fetchAllMarketData } from "./marketValuesSlice";
import { getFinanceRequestConfig } from "../logic/supportFunctions";
import axios from "axios";
import { createError } from "../../templates/notifications/Notifications";
import { extractErrorMessage } from "../../utils/errorFunctions";


// ================= HELPERS =================
const createStepInitialState = () => ({
  [con.LEVELS]: { [con.LOWER_BOUND]: 0, [con.UPPER_BOUND]: 1 },
  [con.SPOT]: { [con.AMOUNT]: 0, [con.FREQUENCY]: 0 },
  [con.FWDS]: { [con.AMOUNT]: 0, [con.FREQUENCY]: 0 },
  [con.OPTIONS]: { [con.AMOUNT]: 0, [con.FREQUENCY]: 0 },
  [con.EXPOSURES]: 0,
  [con.COVERAGES]: 0,
  [con.COVERAGES_SPOT]:0,
  [con.COVERAGES_FWD]: 0,
  [con.COVERAGES_OPTION]: 0,
  [con.COVERAGE_PERCENTAGE]: 0,
});

// ================= THUNKS =================

// Thunk Grid Market Values
export const fetchGridMarketValues = createAsyncThunk(
  "market/fetchGridMarketValues",
  async ({payload}, { rejectWithValue }) => {
    try {
        const config = getFinanceRequestConfig();
        const url = con.rest_finance_url + con.grid_market_values_url;
        const res = await axios.post(url, payload, config);
        return res.data;
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err));
    }
  }
);


export const fetchGridStrategy = createAsyncThunk(
  "grid/createGridStrategy",
  async (payload, { rejectWithValue }) => {
    try {
      const config = getFinanceRequestConfig();
      const url = con.rest_finance_url + con.create_grid_strategie_url;
      
      const response = await axios.post(url, payload, config);
      return response.data;
    } catch (err) {
      return rejectWithValue(extractErrorMessage(err, "Error calculando la estrategia"));
    }
  }
);



// ================= INITIAL STATE =================
const initialState = {
    [con.UPPER_BOUND]: 6000,
    [con.LOWER_BOUND]: 2000,
    [con.VOLATILITY]: 50,
    [con.SL]: 5500,
    [con.START_LEVEL]: 5000,
    [con.GRID_REFERENCE_VALUE]: 5000,
    [con.GRID_VIEW]: con.BULLISH,
    [con.TARGET]: null,
    [con.TARGET_BAND]: 0.4,
    [con.GRID_LEVELS]: {},
    [con.GRID_STRATEGY]: {
        [con.STEP_1]: createStepInitialState(),
        [con.STEP_2]: createStepInitialState(),
        [con.STEP_3]: createStepInitialState(),
        [con.STEP_4]: createStepInitialState(),
    },
    [con.ERROR]: null,
    [con.LOADING]: false,
    [con.ERROR_TYPE]: null,
    // Manual overrides: only cells the operator edited, keyed "step_X.instrument"
    [con.MANUAL_OVERRIDES]: {},

};

// ================= SLICE =================

const gridStrategySlice = createSlice({
  name: con.REDUCER_GRID_STRATEGY,
  initialState,
  reducers: {
    setGridState: (state, action) => {
      const { id, value } = action.payload;
      state[id] = value;
    },

    // Record a manual edit for one cell. A cell holds either an amount OR a
    // frequency (mutually exclusive, mirroring the backend contract) — the
    // last edited field wins. An empty/zero value removes the override.
    setManualOverride: (state, action) => {
      const { step, instrument, field, value } = action.payload;
      const key = `${step}.${instrument}`;
      const numericValue = Number(value);

      // Empty string / null / undefined / NaN → remove the override.
      // An explicit 0 is kept: it means "skip this instrument in this step".
      if (value === '' || value === null || value === undefined || isNaN(numericValue)) {
        delete state[con.MANUAL_OVERRIDES][key];
      } else {
        state[con.MANUAL_OVERRIDES][key] = { field, value: numericValue };
      }
    },

    // Drop the overrides of one instrument (used when the operator flips the
    // Monto/Frecuencia toggle: stale values of the other field must not be sent)
    clearInstrumentOverrides: (state, action) => {
      const instrument = action.payload;
      for (const key of Object.keys(state[con.MANUAL_OVERRIDES])) {
        if (key.endsWith(`.${instrument}`)) {
          delete state[con.MANUAL_OVERRIDES][key];
        }
      }
    },

    clearManualOverrides: (state) => {
      state[con.MANUAL_OVERRIDES] = {};
    },
  },
  
  extraReducers: (builder) => {
    builder

        // ================= UPDATE MARKET DATA =================
        .addCase(fetchAllMarketData.fulfilled, (state, action) => {
            // Reference value → EMA5 (same indicator cassandra uses)
            const referenceValue = action.payload[con.GRID_REFERENCE_VALUE];
            if (referenceValue !== undefined && referenceValue !== null) {
                state[con.GRID_REFERENCE_VALUE] = Number(referenceValue.toFixed(2));
            }
            // View grid
            const gridView = action.payload[con.GRID_VIEW];
            if (gridView !== undefined && gridView !== null) {
                state[con.GRID_VIEW] = gridView;
            }
        })
        
        // ================= UPDATE MARKET VALUES =================
        .addCase(fetchGridMarketValues.fulfilled, (state, action) => {
          state[con.LOADING] = false

          // ── Zero exposure guard ───────────────────────────────────────
          const errorWarning = action.payload[con.WARNINGS]?.find(w => w[con.TYPE] === "error")
          if (errorWarning) {
              state[con.ERROR_TYPE] = "empty_exposures"
              return
          }
          // ─────────────────────────────────────────────────────────────  
          
          const {volatility, lower_bound, upper_bound, step_1_levels, step_2_levels, step_3_levels, step_4_levels, target, target_band} = action.payload;
            state[con.LOWER_BOUND] = lower_bound
            state[con.UPPER_BOUND] = upper_bound
            state[con.VOLATILITY] = volatility
            state[con.GRID_LEVELS] = {
                step_1: step_1_levels,
                step_2: step_2_levels,
                step_3: step_3_levels,
                step_4: step_4_levels,
            }
            state[con.TARGET] = target
            state[con.TARGET_BAND] = target_band
        })

        .addCase(fetchGridMarketValues.pending, (state) => {
          state[con.LOADING] = true;
          state[con.ERROR] = null;
          state[con.ERROR_TYPE] = null;
        })

        .addCase(fetchGridMarketValues.rejected, (state, action) => {
          state[con.LOADING] = false;
          state[con.ERROR] = action.payload;
          state[con.ERROR_TYPE] = null 
          if (action.payload === "empty_exposures" || action.payload === "empty_coverages") {
            state[con.ERROR_TYPE] = action.payload;
          } else {
            createError(action.payload);
          }
        })
        // ================= UPDATE GRID STRATEGY =================
        .addCase(fetchGridStrategy.pending, (state) => {
          state[con.LOADING] = true;
          state[con.ERROR] = null;
          state[con.ERROR_TYPE] = null;
        })

        .addCase(fetchGridStrategy.fulfilled, (state, action) => {
            state[con.LOADING] = false;
            state[con.ERROR] = null;
            state[con.ERROR_TYPE] = null;
            const {upper_bound, lower_bound, volatility, SL, start_level, reference_value, view, target, grid_strategy } = action.payload;
            state[con.UPPER_BOUND] = upper_bound
            state[con.LOWER_BOUND] = lower_bound
            state[con.VOLATILITY] = volatility
            state[con.SL] = SL
            state[con.START_LEVEL] = start_level
            state[con.GRID_REFERENCE_VALUE] = reference_value
            state[con.GRID_VIEW] = view
            state[con.TARGET] = target
            state[con.GRID_STRATEGY] = grid_strategy
        })

        .addCase(fetchGridStrategy.rejected, (state, action) => {
          state[con.LOADING] = false;
          state[con.ERROR] = action.payload;
          // Identificar el tipo de error
          if (action.payload === "empty_exposures" || action.payload === "empty_coverages") {
            state[con.ERROR_TYPE] = action.payload;
          } else {
            createError(action.payload);
          }
        })
  },
  
});

// ================= EXPORTS =================

export const {
  setGridState,
  setManualOverride,
  clearInstrumentOverrides,
  clearManualOverrides,
} = gridStrategySlice.actions;

export default gridStrategySlice.reducer;